'use client';

import { useMemo } from 'react';

import type { GraphQLSupporterGiftLimits } from '@sola/schema/dotcom/supporters/schema.generated.ts';
import { isFinalGiftEligible as isFinalGiftLimitEligible } from '@sola/shared-utils/my/gifts-type-guards.ts';
import { useSupporterQuery } from '@sola/supporter-sdk/hooks/base.ts';
import { giftLimitsQuery } from '@sola/supporter-sdk/queries.ts';

export type GiftEligibility = {
  isPending: boolean;
  isFinalGiftEligible: (childGlobalId: string | null | undefined) => boolean;
};

type GiftEligibilityMethods = Pick<GiftEligibility, 'isFinalGiftEligible'>;

const createGiftEligibility = (
  giftLimits: GraphQLSupporterGiftLimits | undefined,
): GiftEligibilityMethods => {
  const giftTypesByChildGlobalId = new Map(
    (giftLimits?.children ?? []).map(
      ({ id, giftTypes }) => [id, giftTypes] as const,
    ),
  );

  return {
    isFinalGiftEligible: (childGlobalId) => {
      const finalGiftLimits = childGlobalId
        ? (giftTypesByChildGlobalId.get(childGlobalId)?.Final ?? undefined)
        : undefined;

      return isFinalGiftLimitEligible(finalGiftLimits);
    },
  };
};

export const useGiftEligibility = (): GiftEligibility => {
  const { data: giftLimits, isPending } = useSupporterQuery(giftLimitsQuery);
  const giftEligibility = useMemo(
    () => createGiftEligibility(giftLimits),
    [giftLimits],
  );

  return { isPending, ...giftEligibility };
};
